import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAvailableBatchesForDispense,
  qEncounterPharmacyDispenseHistory,
  qEncounterPharmacyQueue,
  qEncounterPharmacySummary,
  type AppConfigRow,
  type PatientPharmacyDispenseHistoryRow,
  type PatientPharmacySummaryRow,
  type PharmacyBatchOptionRow,
  type PharmacyQueueRow,
} from '@helios/data-access';
import { useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  PharmacyLabelPrintMount,
  type PharmacyLabelData,
} from '../../inventory/PharmacyLabelPrint';
import {
  PrescriptionPrintMount,
  type PrescriptionPrintRequest,
} from '../../inventory/PrescriptionPrintMount';

// PRD.201 — Encounter-scoped Pharmacy tab. Same dispense write path as
// global Pharmacy and Patient Pharmacy — only the read queries are
// scoped to the current encounter. Confirm Dispense still inserts the
// canonical three rows: pharmacy_dispenses, pharmacy_dispense_lines,
// and an outbound stock_movements with source_type='pharmacy_dispense'.

interface QueueLineState {
  batchId: string | null;
  batchNumber: string | null;
  batchExpiry: string | null;
  batchAvailable: number | null;
  message: { kind: 'ok' | 'error'; text: string } | null;
  saving: boolean;
}

interface EncounterPharmacyTabProps {
  patientId: string;
  encounterId: string;
  hideHeader?: boolean;
}

export function EncounterPharmacyTab({
  patientId,
  encounterId,
  hideHeader,
}: EncounterPharmacyTabProps) {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  // PRD.202 — clinic name for the medicine label header.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const clinicName = appConfigRows[0]?.org_name ?? null;
  const [labelToPrint, setLabelToPrint] = useState<PharmacyLabelData | null>(null);
  const [rxPrintRequest, setRxPrintRequest] = useState<PrescriptionPrintRequest | null>(null);

  const { data: queueRows = [] } = usePowerSyncQuery<PharmacyQueueRow>(
    qEncounterPharmacyQueue(patientId, encounterId),
  );
  const { data: historyRows = [] } = usePowerSyncQuery<PatientPharmacyDispenseHistoryRow>(
    qEncounterPharmacyDispenseHistory(patientId, encounterId),
  );
  const { data: summaryRows = [] } = usePowerSyncQuery<PatientPharmacySummaryRow>(
    qEncounterPharmacySummary(patientId, encounterId),
  );
  const summary = summaryRows[0] ?? { ready_count: 0, partial_count: 0, dispensed_count: 0 };

  const [search, setSearch] = useState('');
  const [batchModalLineId, setBatchModalLineId] = useState<string | null>(null);
  const [lineState, setLineState] = useState<Record<string, QueueLineState>>({});

  useEffect(() => {
    const validIds = new Set(queueRows.map((r) => r.invoice_item_id));
    setLineState((prev) => {
      const next: Record<string, QueueLineState> = {};
      for (const [k, v] of Object.entries(prev)) if (validIds.has(k)) next[k] = v;
      return next;
    });
  }, [queueRows]);

  function patchLine(invoiceItemId: string, patch: Partial<QueueLineState>) {
    setLineState((prev) => {
      const base: QueueLineState = prev[invoiceItemId] ?? {
        batchId: null,
        batchNumber: null,
        batchExpiry: null,
        batchAvailable: null,
        message: null,
        saving: false,
      };
      return { ...prev, [invoiceItemId]: { ...base, ...patch } };
    });
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return queueRows;
    const q = search.toLowerCase();
    return queueRows.filter((row) => {
      return (
        (row.invoice_no ?? '').toLowerCase().includes(q) ||
        (row.item_name ?? '').toLowerCase().includes(q) ||
        (row.sku ?? '').toLowerCase().includes(q)
      );
    });
  }, [queueRows, search]);

  async function confirmDispense(row: PharmacyQueueRow) {
    if (!db) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Local database not ready.' },
      });
      return;
    }
    if (!orgId) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Missing organization context.' },
      });
      return;
    }
    const state = lineState[row.invoice_item_id];
    if (!state?.batchId) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Select a batch before confirming.' },
      });
      return;
    }
    const qty = row.quantity_remaining ?? 0;
    if (qty <= 0) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'No remaining quantity to dispense.' },
      });
      return;
    }
    if (state.batchAvailable != null && qty > state.batchAvailable) {
      patchLine(row.invoice_item_id, {
        message: {
          kind: 'error',
          text: `Selected batch only has ${state.batchAvailable} available.`,
        },
      });
      return;
    }

    patchLine(row.invoice_item_id, { saving: true, message: null });
    try {
      const dispenseId = crypto.randomUUID();
      const lineId = crypto.randomUUID();
      const now = new Date().toISOString();
      const dispenseNumber = `DISP-${now.slice(0, 10)}-${dispenseId.slice(0, 4)}`;
      const unit = row.inventory_unit ?? row.unit_snapshot ?? null;
      const itemName = row.item_name ?? row.description_snapshot ?? '';

      // 1) Header — encounter_id pulled from the queue row so the dispense
      //    is anchored to the current visit context.
      await db.execute(
        `INSERT INTO pharmacy_dispenses (
          id, org_id, dispense_number, patient_id, encounter_id, invoice_id,
          prescription_id, status, dispensed_at, dispensed_by, remarks,
          created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          dispenseId,
          orgId,
          dispenseNumber,
          row.patient_id,
          row.encounter_id ?? encounterId,
          row.invoice_id,
          null,
          'confirmed',
          now,
          userName || userId || null,
          null,
          now,
          now,
        ],
      );

      // 2) Line
      await db.execute(
        `INSERT INTO pharmacy_dispense_lines (
          id, org_id, pharmacy_dispense_id, invoice_item_id, prescription_line_id,
          inventory_item_id, inventory_batch_id,
          item_name_snapshot, sku_snapshot, batch_number_snapshot, expiry_date_snapshot,
          unit_snapshot, instructions_snapshot,
          quantity_to_dispense, quantity_dispensed,
          status, remarks, created_at, updated_at
        ) VALUES (
          ?, ?, ?, ?, ?,
          ?, ?,
          ?, ?, ?, ?,
          ?, ?,
          ?, ?,
          ?, ?, ?, ?
        )`,
        [
          lineId,
          orgId,
          dispenseId,
          row.invoice_item_id,
          null,
          row.inventory_item_id,
          state.batchId,
          itemName,
          row.sku,
          state.batchNumber,
          state.batchExpiry,
          unit,
          null,
          qty,
          qty,
          'confirmed',
          null,
          now,
          now,
        ],
      );

      // 3) Outbound stock movement — the actual stock-out event.
      await db.execute(
        `INSERT INTO stock_movements (
          id, org_id, inventory_item_id, inventory_batch_id,
          source_type, source_id, source_line_id,
          movement_type, quantity_in, quantity_out, unit,
          movement_date, remarks, created_by, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          row.inventory_item_id,
          state.batchId,
          'pharmacy_dispense',
          dispenseId,
          lineId,
          'pharmacy_dispense',
          0,
          qty,
          unit,
          now.slice(0, 10),
          null,
          userId || null,
          now,
          now,
        ],
      );

      patchLine(row.invoice_item_id, {
        saving: false,
        message: { kind: 'ok', text: 'Dispensed.' },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Confirm failed. Please try again.';
      patchLine(row.invoice_item_id, {
        saving: false,
        message: { kind: 'error', text: msg },
      });
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-5)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Pharmacy
          </h2>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            Dispense Medicine invoice lines for this visit. Confirming a dispense is the only
            stock-out event.
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 'var(--sp-3)',
        }}
      >
        <SummaryCard label="Ready for Dispense" value={summary.ready_count ?? 0} tone="info" />
        <SummaryCard
          label="Partially Dispensed"
          value={summary.partial_count ?? 0}
          tone="warning"
        />
        <SummaryCard label="Dispensed" value={summary.dispensed_count ?? 0} tone="success" />
      </div>

      {/* Search */}
      <div>
        <input
          type="text"
          placeholder="Search medicine, SKU, or invoice..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            maxWidth: 360,
            height: 'var(--input-height)',
            padding: '0 var(--input-padding-x)',
            borderRadius: 'var(--input-radius)',
            border: '1px solid var(--border-input)',
            background: 'var(--input-bg)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--input-text)',
            outline: 'none',
          }}
        />
      </div>

      {/* Pending list */}
      <SectionTitle>Ready to Dispense</SectionTitle>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {filtered.length === 0 ? (
          <EmptyState
            title={
              queueRows.length === 0
                ? 'No medicines ready to dispense for this visit.'
                : 'No matching results.'
            }
            hint={
              queueRows.length === 0
                ? 'Medicine invoice lines appear here once the visit invoice is paid or cleared for Pharmacy.'
                : 'Try a different search.'
            }
          />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Medicine</Th>
                <Th>Invoice</Th>
                <Th>Batch</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Dispensed</Th>
                <Th align="right">Remaining</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const state = lineState[row.invoice_item_id];
                const batchSelected = !!state?.batchId;
                const overAvailable =
                  state?.batchAvailable != null &&
                  (row.quantity_remaining ?? 0) > state.batchAvailable;
                const isPartial = (row.qty_dispensed ?? 0) > 0;
                return (
                  <tr key={row.invoice_item_id} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                        }}
                      >
                        {row.item_name ?? row.description_snapshot ?? '—'}
                      </div>
                      {row.sku && (
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          SKU: {row.sku}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                        }}
                      >
                        {row.invoice_no ?? row.invoice_id?.slice(0, 8) ?? '—'}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {row.invoice_date ?? ''}
                      </div>
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => setBatchModalLineId(row.invoice_item_id)}
                        style={{
                          height: 32,
                          padding: '0 8px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border)',
                          background: batchSelected
                            ? 'var(--color-success-bg)'
                            : 'var(--bg-surface)',
                          color: batchSelected ? 'var(--color-success-text)' : 'var(--text-muted)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 500,
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'left',
                        }}
                      >
                        {batchSelected
                          ? `${state.batchNumber}${
                              state?.batchAvailable != null
                                ? ` · ${state.batchAvailable} avail`
                                : ''
                            }`
                          : 'Select batch…'}
                      </button>
                    </Td>
                    <Td align="right">{row.qty_ordered ?? 0}</Td>
                    <Td align="right">{row.qty_dispensed ?? 0}</Td>
                    <Td align="right">
                      <span
                        style={{
                          fontWeight: 'var(--font-weight-medium)',
                          color: overAvailable ? '#991b1b' : 'var(--text)',
                        }}
                      >
                        {row.quantity_remaining ?? 0}{' '}
                        {row.inventory_unit ?? row.unit_snapshot ?? ''}
                      </span>
                    </Td>
                    <Td>
                      <StatusBadge
                        kind={isPartial ? 'partial' : 'ready'}
                        label={
                          isPartial
                            ? `Partial (${row.qty_dispensed}/${row.qty_ordered ?? 0})`
                            : 'Ready'
                        }
                      />
                    </Td>
                    <Td align="right">
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 4,
                        }}
                      >
                        <Button
                          variant="gradient"
                          size="md"
                          onClick={() => confirmDispense(row)}
                          disabled={!batchSelected || state?.saving === true || overAvailable}
                        >
                          {state?.saving ? 'Saving...' : 'Confirm Dispense'}
                        </Button>
                        {state?.message && (
                          <span
                            style={{
                              fontSize: 10,
                              color:
                                state.message.kind === 'ok'
                                  ? 'var(--color-success-text)'
                                  : '#991b1b',
                            }}
                          >
                            {state.message.text}
                          </span>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Dispense history */}
      <SectionTitle>Dispense History (this visit)</SectionTitle>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {historyRows.length === 0 ? (
          <EmptyState
            title="No dispenses yet for this visit."
            hint="Confirmed dispenses for this encounter appear here automatically."
          />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Dispense No.</Th>
                <Th>Medicine</Th>
                <Th>Batch</Th>
                <Th>Expiry</Th>
                <Th align="right">Qty</Th>
                <Th>Invoice</Th>
                <Th>Dispensed At</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map((row) => (
                <tr
                  key={row.pharmacy_dispense_line_id}
                  style={{ borderTop: '1px solid var(--border)' }}
                >
                  <Td>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text)',
                      }}
                    >
                      {row.dispense_number ?? row.pharmacy_dispense_id?.slice(0, 8) ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text)',
                      }}
                    >
                      {row.item_name_snapshot ?? '—'}
                    </div>
                    {row.sku_snapshot && (
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        SKU: {row.sku_snapshot}
                      </div>
                    )}
                  </Td>
                  <Td>{row.batch_number_snapshot ?? '—'}</Td>
                  <Td>{row.expiry_date_snapshot ?? '—'}</Td>
                  <Td align="right">
                    {row.quantity_dispensed ?? 0} {row.unit_snapshot ?? ''}
                  </Td>
                  <Td>{row.invoice_no ?? row.invoice_id?.slice(0, 8) ?? '—'}</Td>
                  <Td>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {row.dispensed_at ? row.dispensed_at.replace('T', ' ').slice(0, 16) : '—'}
                    </span>
                  </Td>
                  <Td align="right">
                    <PrintActions
                      onPrintPrescription={() => setRxPrintRequest({ patientId, encounterId })}
                      onPrintLabel={() =>
                        setLabelToPrint({
                          patientName: row.patient_name ?? '',
                          medicineName: row.item_name_snapshot ?? '',
                          sku: row.sku_snapshot,
                          instructions: row.instructions_snapshot,
                          qty: row.quantity_dispensed,
                          unit: row.unit_snapshot,
                          batchNumber: row.batch_number_snapshot,
                          expiryDate: row.expiry_date_snapshot,
                          dispensedAt: row.dispensed_at,
                          dispenseNumber: row.dispense_number,
                          clinicName,
                        })
                      }
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PharmacyLabelPrintMount data={labelToPrint} onAfterPrint={() => setLabelToPrint(null)} />
      <PrescriptionPrintMount
        request={rxPrintRequest}
        onAfterPrint={() => setRxPrintRequest(null)}
      />

      {batchModalLineId && (
        <BatchPickerModal
          itemId={
            queueRows.find((r) => r.invoice_item_id === batchModalLineId)?.inventory_item_id ?? null
          }
          requiredQty={
            queueRows.find((r) => r.invoice_item_id === batchModalLineId)?.quantity_remaining ?? 0
          }
          portalStyle={brandTokens as CSSProperties}
          onClose={() => setBatchModalLineId(null)}
          onSelect={(batch) => {
            patchLine(batchModalLineId, {
              batchId: batch.inventory_batch_id,
              batchNumber: batch.batch_number,
              batchExpiry: batch.expiry_date,
              batchAvailable: batch.available_quantity,
              message: null,
            });
            setBatchModalLineId(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Local presentation helpers ───

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: 'var(--font-size-md)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text)',
      }}
    >
      {children}
    </h3>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'info' | 'warning' | 'success';
}) {
  const palette = {
    info: { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' },
    warning: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
    success: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  }[tone];
  return (
    <div
      style={{
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-bold, 700)',
          color: palette.color,
        }}
      >
        {value}
      </span>
      <span
        style={{
          width: 24,
          height: 4,
          borderRadius: 2,
          background: palette.bg,
        }}
      />
    </div>
  );
}

function StatusBadge({ kind, label }: { kind: 'ready' | 'partial'; label: string }) {
  const palette =
    kind === 'partial'
      ? { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }
      : { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--radius-full)',
        background: palette.bg,
        color: palette.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
      }}
    >
      {label}
    </span>
  );
}

function EmptyState({ title, hint }: { title: string; hint: string }) {
  return (
    <div
      style={{
        padding: 'var(--sp-8)',
        textAlign: 'center',
        color: 'var(--text-muted)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-md)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)' }}>{hint}</div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 12px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: 'var(--bg-surface-2, #fafafa)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '10px 12px',
        textAlign: align,
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

// PRD.202 — print-action pair shown on each dispense-history row.
function PrintActions({
  onPrintPrescription,
  onPrintLabel,
}: {
  onPrintPrescription: (() => void) | null;
  onPrintLabel: () => void;
}) {
  const baseStyle: React.CSSProperties = {
    height: 28,
    padding: '0 8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    cursor: 'pointer',
  };
  return (
    <div style={{ display: 'inline-flex', gap: 6 }}>
      <button
        type="button"
        onClick={onPrintPrescription ?? undefined}
        disabled={!onPrintPrescription}
        title={
          onPrintPrescription
            ? 'Open the encounter Prescriptions tab to print using the saved layout'
            : "Prescription print is available from the encounter's Prescriptions tab."
        }
        style={{
          ...baseStyle,
          color: onPrintPrescription ? 'var(--text)' : 'var(--text-disabled)',
          cursor: onPrintPrescription ? 'pointer' : 'not-allowed',
        }}
      >
        Print Rx
      </button>
      <button
        type="button"
        onClick={onPrintLabel}
        title="Print medicine label using dispense snapshots"
        style={baseStyle}
      >
        Print Label
      </button>
    </div>
  );
}

// ─── Batch picker modal — same UX as global Pharmacy (PRD.194 §12). ───

function BatchPickerModal({
  itemId,
  requiredQty,
  portalStyle,
  onClose,
  onSelect,
}: {
  itemId: string | null;
  requiredQty: number;
  portalStyle: CSSProperties;
  onClose: () => void;
  onSelect: (batch: PharmacyBatchOptionRow) => void;
}) {
  const { data: batchRows = [] } = usePowerSyncQuery<PharmacyBatchOptionRow>(
    itemId
      ? qAvailableBatchesForDispense(itemId)
      : {
          key: ['pharmacy-available-batches', '__none__'] as const,
          sql: `SELECT NULL AS inventory_batch_id, NULL AS batch_number, NULL AS expiry_date, 0 AS available_quantity WHERE 1=0`,
          params: [],
          watch: ['inventory_batches', 'stock_movements'],
        },
  );

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Select Batch"
      maxWidth={560}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div style={{ padding: 'var(--sp-3) var(--sp-5) var(--sp-5)' }}>
        <p
          style={{
            margin: '0 0 var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Pick an existing batch with at least <strong>{requiredQty}</strong> available. New batches
          are created via Delivery Order receiving or Inventory Adjustments — not from Pharmacy.
        </p>
        {batchRows.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              textAlign: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            No available batches for this medicine. Receive stock or create an inventory adjustment
            first.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Batch Number</Th>
                <Th>Expiry</Th>
                <Th align="right">Available</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {batchRows.map((b) => {
                const avail = b.available_quantity ?? 0;
                const insufficient = avail < requiredQty;
                return (
                  <tr
                    key={b.inventory_batch_id}
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td style={{ padding: '8px 6px', fontSize: 'var(--font-size-sm)' }}>
                      {b.batch_number ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {b.expiry_date ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        textAlign: 'right',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: insufficient ? '#991b1b' : 'var(--text)',
                      }}
                    >
                      {avail}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <Button
                        variant="outline"
                        size="md"
                        disabled={avail <= 0}
                        onClick={() => onSelect(b)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
